/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useUserForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing user form state and changes.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo } from 'react';

export interface UserFormData {
  username: string;
  email: string;
  password: string;
  fullName: string;
  department: string;
  locale: string;
  timezone: string;
  startupDialog: string;
  totpEnabled: boolean;
  isActive: boolean;
  debugMode: boolean;
  isPrimaryAdmin: boolean;
  extensionId: number | null;
  profileId: string;
  logProfileId: string;
  tenantIds: string[];
  chatbotEnabled: boolean;
  chatbotAgentId: number | null;
  appsConfigs: {
    coreEnabled: boolean;
    switchboardEnabled: boolean;
    switchboardRole: string; // admin, operator
    switchboardLogProfileId?: string;
    switchboardUrl: string;
    portalEnabled: boolean;
    portalUrl: string;
  };
}

const initialFormData: UserFormData = {
  username: '',
  email: '',
  password: '',
  fullName: '',
  department: '',
  locale: 'en',
  timezone: 'UTC',
  startupDialog: '/dashboard',
  totpEnabled: false,
  isActive: true,
  debugMode: false,
  isPrimaryAdmin: false,
  extensionId: null,
  profileId: '',
  logProfileId: '',
  tenantIds: [],
  chatbotEnabled: false,
  chatbotAgentId: null,
  appsConfigs: {
    coreEnabled: true,
    switchboardEnabled: true,
    switchboardRole: '',
    switchboardLogProfileId: '',
    switchboardUrl: '',
    portalEnabled: true,
    portalUrl: '',
  },
};

export function useUserForm(mode: 'create' | 'edit') {
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [originalData, setOriginalData] = useState<UserFormData>(initialFormData);

  const handleInputChange = (field: keyof UserFormData, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMultiSelectChange = (field: keyof UserFormData, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    if (mode === 'create') {
      setFormData(initialFormData);
    } else {
      setFormData({ ...originalData });
    }
  };

  const hasChanges = useMemo(() => {
    if (mode === 'create') return true;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [mode, formData, originalData]);

  return {
    formData,
    originalData,
    setFormData,
    setOriginalData,
    handleInputChange,
    handleMultiSelectChange,
    resetForm,
    hasChanges,
  };
}

